import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge'; // For ID card/driver
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

import { useNavigate, useLocation } from "react-router-dom";
import * as customerServices from "../services/customer";
import CarSelectDialog from "./CarSelectDialog";
import { formatThaiId } from "../utils/formatters";

export default function CustomerPickerDialog({
  open,
  onClose,
  showCreateButton = true,
  onSuccess,
  selectedCustomerId = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [carType] = useState("");
  const [licenseFilter] = useState("");
  const [provinceFilter] = useState("");
  const [openCarSelectDialog, setOpenCarSelectDialog] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("search");
    if (q) {
      try {
        setSearch(decodeURIComponent(q));
      } catch {
        setSearch(q);
      }
    }
  }, [location.search]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await customerServices.getCustomers();
      const data = res?.data?.data ?? [];
      setCustomers(data);
      if (selectedCustomerId) {
        const matchedCustomer = data.find(
          (customer) => String(customer.id) === String(selectedCustomerId),
        );
        if (matchedCustomer) {
          setSelectedCustomer(matchedCustomer);
        }
      }
      return data;
    } catch (e) {
      console.error("Error fetching customers:", e);
      setCustomers([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCustomers();

      const params = new URLSearchParams(location.search);
      const q = params.get("search");
      if (q) {
        try {
          setSearch(decodeURIComponent(q));
        } catch {
          setSearch(q);
        }
      }
      params.delete("search");
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate, open]);

  const includesSafe = (a = "", b = "") =>
    String(a).toLowerCase().includes(String(b).toLowerCase());

  const matchCarType = (customer) =>
    !carType || (customer.cars || []).some((car) => car?.type === carType);

  const matchLicense = (customer) =>
    !licenseFilter ||
    (customer.cars || []).some((car) =>
      includesSafe(car?.license, licenseFilter)
    );

  const matchProvince = (customer) =>
    !provinceFilter ||
    (customer.cars || []).some((car) =>
      includesSafe(car?.province, provinceFilter)
    );

  // เพิ่มค้นหาคนขับ: name / surname / fullName
  const matchSearch = (customer) => {
    if (!search) return true;
    const byName = includesSafe(customer.name, search);
    const byCarLicense = (customer.cars || []).some((car) =>
      includesSafe(car?.license, search)
    );
    const byDriverName = (customer.drivers || []).some((d) => {
      const fullName = [d?.name, d?.surname].filter(Boolean).join(" ");
      return (
        includesSafe(d?.name, search) ||
        includesSafe(d?.surname, search) ||
        includesSafe(fullName, search)
      );
    });
    return byName || byCarLicense || byDriverName;
  };

  const filteredData = useMemo(
    () =>
      customers.filter(
        (c) =>
          matchSearch(c) &&
          matchCarType(c) &&
          matchLicense(c) &&
          matchProvince(c)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customers, search, carType, licenseFilter, provinceFilter]
  );

  useEffect(() => {
    if (!open || !selectedCustomerId) return;

    const targetId = String(selectedCustomerId);
    const timer = setTimeout(() => {
      const el = document.getElementById(`customer-card-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [open, selectedCustomerId, filteredData.length]);

  const handleSelect = (customer) => {
    setSelectedCustomer(customer);
    setOpenCarSelectDialog(true);
  };

  const handleCreateNew = () => {
    onClose?.();
    navigate(`/customers/create`);
  };

  const handleCustomerPicked = (customer) => {
    onSuccess?.(customer);
    onClose?.();
  };

  // Helper to get initial
  const getInitial = (name) => {
    return name ? name.charAt(0) : "?";
  };

  // Helper to get random soft color class based on id/name
  const getColorClass = (id) => {
    const colors = [
      "bg-blue-50 text-blue-600",
      "bg-green-50 text-green-600",
      "bg-purple-50 text-purple-600",
      "bg-orange-50 text-orange-600",
      "bg-indigo-50 text-indigo-600",
      "bg-teal-50 text-teal-600"
    ];
    return colors[(id || 0) % colors.length];
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="xl" // Increased width for better grid
        PaperProps={{
            sx: {
              borderRadius: "20px",
              height: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
               backgroundColor: "#f9fafb" // Light background for dialog body
            }
        }}
      >
        {/* Header */}
        <DialogTitle className="bg-white border-b border-gray-100 p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-sm z-10">
            <div>
                 <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-200">
                        <PersonIcon />
                    </span>
                    เลือกลูกค้า
                 </h2>
                 <p className="text-sm text-gray-500 mt-1 pl-12 hidden sm:block">เลือกรายการลูกค้าเพื่อทำรายการต่อ</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto items-center">
                 <div className="relative group w-full sm:w-80">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input 
                        type="text" 
                        placeholder="ค้นหาชื่อ, คนขับ, หรือทะเบียนรถ..." 
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-transparent hover:bg-white hover:border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl text-sm outline-none transition-all shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value.trimStart())}
                    />
                </div>
                
                {showCreateButton && (
                    <button
                        onClick={handleCreateNew}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white font-medium rounded-xl shadow-lg shadow-gray-200 transition-all hover:-translate-y-0.5 active:translate-y-0 active:shadow-none whitespace-nowrap"
                    >
                        <AddIcon fontSize="small" />
                        ลูกค้าใหม่
                    </button>
                )}

                 <button onClick={onClose} className="sm:hidden absolute top-4 right-4 p-2 text-gray-400">
                     <CloseIcon />
                 </button>
            </div>
        </DialogTitle>


        <DialogContent className="p-4 sm:p-6 overflow-y-auto custom-scrollbar">
          {loading ? (
             <div className="flex justify-center items-center h-full min-h-[300px]">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                    <p className="text-gray-500 font-medium">กำลังโหลดข้อมูล...</p>
                </div>
             </div>
          ) : filteredData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 border-2 border-dashed border-gray-200 rounded-2xl bg-white min-h-[400px]">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                    <SearchIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                </div>
                <h3 className="text-lg font-bold text-gray-700">ไม่พบลูกค้าที่ค้นหา</h3>
                <p className="text-gray-500 mt-1 max-w-xs mx-auto">ลองเปลี่ยนคำค้นหา หรือกดปุ่ม "ลูกค้าใหม่" เพื่อเพิ่มข้อมูล</p>
                {showCreateButton && (
                    <button 
                        onClick={handleCreateNew}
                        className="mt-6 text-blue-600 font-medium hover:underline"
                    >
                        + เพิ่มลูกค้าใหม่
                    </button>
                )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 pb-8">
              {filteredData.map((item) => {
                 const isSelected =
                   String(selectedCustomer?.id ?? "") === String(item.id);
                 return (
                    <div
                      key={item.id}
                      id={`customer-card-${item.id}`}
                      onClick={() => handleSelect(item)}
                      className={`group bg-white rounded-2xl p-5 border shadow-sm hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 cursor-pointer flex flex-col relative overflow-hidden ${
                        isSelected
                          ? "border-blue-500 ring-4 ring-blue-100 scale-[1.01]"
                          : "border-gray-100 hover:border-blue-300"
                      }`}
                    >
                       <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                       {isSelected && (
                         <div className="absolute top-3 right-3 text-xs font-bold px-2 py-1 rounded-full bg-blue-700 text-white shadow-lg">
                           ลูกค้าที่เลือกอยู่
                         </div>
                       )}
                       
                       {/* Customer Header */}
                       <div className="flex items-start gap-4 mb-4">
                            <div className={`w-12 h-12 rounded-2xl ${getColorClass(item.id)} flex items-center justify-center text-xl font-bold shadow-inner shrink-0`}>
                                {getInitial(item.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-bold text-gray-800 line-clamp-2 leading-tight mb-1 group-hover:text-blue-600 transition-colors" title={item.name}>
                                    {item.name}
                                </h3>
                                <div className="flex flex-wrap gap-1.5">
                                    <span className={`text-[10px] px-2 py-0.5 rounded-md font-medium border ${
                                        item.personType === "normal" 
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-100" 
                                        : "bg-indigo-50 text-indigo-700 border-indigo-100"
                                    }`}>
                                        {item.personType === "normal" ? "บุคคลธรรมดา" : "นิติบุคคล"}
                                    </span>
                                    {item.group && (
                                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 border border-gray-200">
                                            {item.group}
                                        </span>
                                    )}
                                </div>
                            </div>
                       </div>

                       <div className="space-y-4 flex-1">
                           {/* Cars List */}
                           <div>
                                <h4 className="text-xs font-semibold text-gray-400 mb-2 flex items-center gap-1 uppercase tracking-wider">
                                    <DirectionsCarIcon sx={{ fontSize: 14 }} />
                                    รถที่ลงทะเบียนไว้
                                </h4>
                                <div className="bg-gray-50 rounded-xl p-2 min-h-[60px] max-h-[100px] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 space-y-1.5 border border-gray-100">
                                    {item.cars && item.cars.length > 0 ? item.cars.map((car, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-xs text-gray-600 bg-white p-1.5 rounded-lg border border-gray-100 shadow-sm">
                                            <LocalShippingIcon sx={{ fontSize: 12 }} className="text-gray-400 shrink-0" />
                                            <span className="font-medium truncate flex-1">
                                                {car.license}
                                            </span>
                                            {car.province && (
                                                <span className="text-[10px] text-gray-400 truncate max-w-[80px]">
                                                    ({car.province})
                                                </span>
                                            )}
                                        </div>
                                    )) : (
                                        <div className="h-full flex items-center justify-center text-xs text-gray-300 italic py-2">
                                            ไม่มีรถ
                                        </div>
                                    )}
                                </div>
                           </div>
                           
                           {/* Drivers List */}
                           <div>
                                <h4 className="text-xs font-semibold text-gray-400 mb-2 flex items-center gap-1 uppercase tracking-wider">
                                    <BadgeIcon sx={{ fontSize: 14 }} />
                                    คนขับที่ลงทะเบียนไว้
                                </h4>
                                <div className="bg-gray-50 rounded-xl p-2 min-h-[60px] max-h-[100px] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 space-y-1.5 border border-gray-100">
                                    {item.drivers && item.drivers.length > 0 ? item.drivers.map((d, idx) => {
                                        const fullName = [d.name, d.surname].filter(Boolean).join(" ");
                                        return (
                                            <div key={idx} className="flex items-center gap-2 text-xs text-gray-600 bg-white p-1.5 rounded-lg border border-gray-100 shadow-sm">
                                                <PersonIcon sx={{ fontSize: 12 }} className="text-gray-400 shrink-0" />
                                                <span className="font-medium truncate">
                                                    {fullName}
                                                </span>
                                            </div>
                                        );
                                    }) : (
                                        <div className="h-full flex items-center justify-center text-xs text-gray-300 italic py-2">
                                            ไม่ระบุคนขับ
                                        </div>
                                    )}
                                </div>
                           </div>
                       </div>
                       
                       <div className="mt-4 pt-3 border-t border-gray-50 flex items-center justify-end">
                            <span className="text-xs font-medium text-blue-600 group-hover:translate-x-1 transition-transform flex items-center">
                                เลือกรายการนี้ →
                            </span>
                       </div>
                    </div>
                 );
              })}
            </div>
          )}
        </DialogContent>

        <DialogActions className="bg-white border-t border-gray-100 p-4 z-10">
          <button 
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-all text-sm ml-auto"
          >
            ปิดหน้าต่าง
          </button>
        </DialogActions>
      </Dialog>

      <CarSelectDialog
        open={openCarSelectDialog}
        onClose={() => setOpenCarSelectDialog(false)}
        customerData={selectedCustomer}
        fetchData={fetchCustomers}
        onSuccess={handleCustomerPicked}
      />
    </>
  );
}
